import { Inject, Injectable, NgZone } from '@angular/core';
import { ToastService } from '@captiveaire/toast';
import { AppRootScope, EventTypes } from '../../src';
import { $log, $q, $timeout } from '../ajs-upgraded-providers';
import { AlertCategories } from '../alerts/typings/AlertCategories';
import { CardStatus } from '../alerts/typings/CardStatus';
import { CardStatusUpdateInfo } from '../alerts/typings/CardStatusUpdateInfo';
import { IDeviceAutoCommission } from '../alerts/typings/IDeviceAutoCommission';
import { INotificationService } from '../alerts/typings/INotificationService';
import {
    AlertAggregateUpdateInfo,
    DeviceSyncUpdateInfo,
    LoadingStatusUpdateInfo,
    SensorUpdateInfo
} from '../alerts/typings/models';
import { SubscriptionIdTypeGetters } from '../alerts/typings/SubscriptionIdTypeGetters';
import { SubscriptionIdTypes } from '../alerts/typings/SubscriptionIdTypes';
import { SubscriptionTypes } from '../alerts/typings/SubscriptionTypes';
import { ViewModelUpdateInfo } from '../alerts/typings/ViewModelUpdateInfo';
import { AppContextService } from '../appContext/AppContextService';
import { accessTokenSourceToken, IAccessTokenSource } from '../authentication/AccessTokenSource';
import { CommonBroadcastService } from '../common-broadcast/common-broadcast.service';
import { NotificationTransitionsService } from './NotificationTransitionsService';

// This service connects to the server's Signal-R endpoint and listens
// for notifications.  These handlers then publish local events that
// can subscribed to by other application components.  The rest of the
// application doesn't have any dependency on Signal-R.
@Injectable({
    providedIn: 'root'
})
// tslint:disable:member-ordering
export class NotificationService implements INotificationService {
    public static $name: string = 'notificationService';

    private _hubConnection: SignalR.Hub.Connection;
    private _locationHubProxy: SignalR.Hub.Proxy;

    private _useImplicitAuth = true;

    private _tryingToReconnect = false;
    private _abortRetry = false;
    public subscriptionKeys = new Map<SubscriptionTypes, string>();

    // Mapping for event name to enumeration and handler.
    public subscriptionHandlers = {
        Alert: {
            category: AlertCategories.Monitoring,
            handler: this.broadcastAlertUpdate
        },

        RtuStatus: {
            category: AlertCategories.Controls,
            handler: this.broadcastRtuStatusUpdate
        },

        Sensor: {
            category: AlertCategories.Monitoring,
            handler: this.broadcastSensorUpdate
        },

        ViewModel: {
            category: AlertCategories.Controls,
            handler: this.broadcastViewModelUpdate
        },

        LoadingStatus: {
            handler: this.broadcastLoadingStatusUpdate,
        },

        HardwareItemSyncStatus: {
            handler: this.broadcastHardwareItemSyncStatusUpdate
        },

        DeviceHardwareItemSyncSuccess: {
            handler: this.broadcastDeviceSyncUpdate
        },

        DeviceStatusUpdate: {
            handler: this.broadcastDeviceStatusUpdate
        },

        DeviceAutoCommission: {
            handler: this.broadcastDeviceAutoCommission
        },

        AreaAssignment: {
            handler: this.broadcastAreaAssignment
        },

        ModuleRegistrationAlertSubscription: {
            handler: this.broadcastModuleRegistrationAlertSubscription
        },

        DeviceRemoved: {
            handler: this.broadcastDeviceSyncUpdate
        },

        JobProfileUpserted: {
            handler: this.jobProfileUpserted
        }
    };

    // The current active submissions.
    private _currentSubscriptions = { Controls: [], Monitoring: [], Location: [] };

    private _connectionPromise: angular.IPromise<string>;

    private readonly toastDuration = 4000;
    private readonly initialRetryDelay = 10000;
    private readonly linearRetryLimit = 10;
    private readonly exponentialRetryCoefficient = 2;
    private retryDelay = this.initialRetryDelay;
    private linearRetryCount = 0;
    private showToasts: boolean;

    constructor(
        @Inject('$rootScope') public $rootScope: AppRootScope,
        @Inject($q) private $q: angular.IQService,
        @Inject($timeout) private $timeout: angular.ITimeoutService,
        @Inject($log) private $log: angular.ILogService,
        @Inject(accessTokenSourceToken) private accessTokenSource: IAccessTokenSource,
        private appContextService: AppContextService,
        private toastService: ToastService,
        private commonBroadcastService: CommonBroadcastService,
        private notificationTransitionsService: NotificationTransitionsService,
        private zone: NgZone
    ) {
    }

    public initialize(url?: string, options?: SignalR.Hub.Options, useImplicitAuth: boolean = true, registerAlertHandler: boolean = true) {
        this.zone.runOutsideAngular(() => {
            this._useImplicitAuth = useImplicitAuth;
            this._hubConnection = $.hubConnection(url, options);
            this._locationHubProxy = this._hubConnection.createHubProxy('locationHub');
            // don't show connection status toasts on pwa
            this.showToasts = registerAlertHandler;
            this.registration(registerAlertHandler);
            this._connectionPromise = this.connect();
        });
    }

    private registration(registerAlertHandler: boolean) {
        this.registerHubEventHandlers(registerAlertHandler);
        this.registerConnStateEventHandlers();
        this.notificationTransitionsService.initialize(this);
    }

    // ------------------------------------
    // Client Hub Event Handlers
    // ------------------------------------
    // When the SignalR callback is called, publish a local event so
    // all subscribers can be notified.
    private registerHubEventHandlers(registerAlertHandler: boolean) {
        this.subscriptionKeys.set(SubscriptionTypes.AlertAggregate, 'AlertAggregate');
        this.subscriptionKeys.set(SubscriptionTypes.Area, 'Area');
        this.subscriptionKeys.set(SubscriptionTypes.Alert, 'Alert');
        this.subscriptionKeys.set(SubscriptionTypes.Sensor, 'Sensor');
        this.subscriptionKeys.set(SubscriptionTypes.RtuStatus, 'RtuStatus');
        this.subscriptionKeys.set(SubscriptionTypes.SensorUpdate, 'SensorUpdate');
        this.subscriptionKeys.set(SubscriptionTypes.ViewModel, 'ViewModel');
        this.subscriptionKeys.set(SubscriptionTypes.UserSpecificLoadingStatus, 'LoadingStatus');
        this.subscriptionKeys.set(SubscriptionTypes.HardwareItemSyncLoadingStatus, 'HardwareItemSyncStatus');
        this.subscriptionKeys.set(SubscriptionTypes.DeviceStatus, 'DeviceStatusUpdate');
        this.subscriptionKeys.set(SubscriptionTypes.DeviceAutoCommission, 'DeviceAutoCommission');
        this.subscriptionKeys.set(SubscriptionTypes.AreaAssignment, 'AreaAssignment');
        this.subscriptionKeys.set(SubscriptionTypes.ModuleRegistrationAlertSubscription, 'ModuleRegistrationAlertSubscription');
        this.subscriptionKeys.set(SubscriptionTypes.DeviceHardwareItemSyncSuccess, 'DeviceHardwareItemSyncSuccess');
        this.subscriptionKeys.set(SubscriptionTypes.DeviceRemoved, 'DeviceRemoved');
        this.subscriptionKeys.set(SubscriptionTypes.JobProfileUpserted, 'JobProfileUpserted');

        if (registerAlertHandler) {
            this.subscriptionHandlers['AlertAggregate'] = {
                category: AlertCategories.Location,
                handler: this.broadcastAlertAggregateUpdate
            };
        }

        /* It is ESSENTIAL that this code be executed before the connection is made so that
         * SignalR will actually be able to call it from the server
         */
        this._locationHubProxy.on('updateSubscription', (locationId, subscriptionType, data, sensorInfo) => {
            this.onUpdateSubscription(locationId, subscriptionType, data, sensorInfo);
        });
    }

    // ------------------------------------
    // Connection Management
    // ------------------------------------
    private registerConnStateEventHandlers() {
        this._hubConnection.reconnected(() => this.reconnected());
        this._hubConnection.reconnecting(() => this.reconnecting());
        this._hubConnection.disconnected(() => this.disconnected());
    }

    public stopAllSubscriptions(alertCategory: string) {
        const currSubscriptions = this._currentSubscriptions[alertCategory];

        const len: number = currSubscriptions.length;

        for (let i = 0; i < len; i++) {
            const subscription = currSubscriptions.shift();

            this.stopUpdates(subscription.locationId, subscription.subscriptionType, subscription.updateCriteria);
        }
    }

    public restartAllSubscriptions(alertCategory: string) {
        const currSubscriptions = this._currentSubscriptions[alertCategory];

        const len: number = currSubscriptions.length;

        for (let i = 0; i < len; i++) {
            const subscription = currSubscriptions.shift();

            this.startUpdates(
                subscription.locationId,
                subscription.subscriptionType,
                subscription.updateCriteria,
                subscription.subscriptionState);
        }
    }

    // Determine if the user is navigating away form a UI-Router state that is tagged
    // with a alertCategory to another state that has a different or null alert-category.
    public navigatingAwayFromAlertCategory(fromState: any, toState: any): string {
        const fromAlertCategory = fromState && fromState.data && fromState.data.alertCategory;
        const toStateAlertCategory = toState.data && toState.data.alertCategory;

        // tslint:disable-next-line:triple-equals
        if (fromAlertCategory && (fromAlertCategory != toStateAlertCategory)) {
            return fromAlertCategory;
        }
        return null;
    }

    // Makes the connection to the server over which the submission are requested.
    public connect(): angular.IPromise<string> {
        const defer = this.$q.defer<string>();

        // This is for clients that don't support web-sockets.
        const connConfig: SignalR.ConnectionOptions = {
            waitForPageLoad: false,
            withCredentials: this._useImplicitAuth
        };

        (this._hubConnection as any).accessToken = this.accessTokenSource.getAccessToken();

        // Start a connection with the server.
        this._hubConnection.start(connConfig)
            .done(() => {
                this.logConnectionStatus('CASLink service connection is active.');

                if (this._tryingToReconnect) {
                    this.notifyConnectionStatus('CASLink service connection restored.');
                    // Resubscribe to all connections on reconnect
                    this.resubscribeToAllCategories();
                } else {
                    this._tryingToReconnect = true;
                }

                this.resetConnRetry();

                this.notifyConnected();

                defer.resolve($.connection.hub.id);
            })
            .fail(() => {
                this.logConnectionStatus('CASLink could not restore a service connection! CASLink is offline! :( ');
                defer.reject(null);
            });

        return defer.promise;
    }

    public disconnect() {
        this._abortRetry = true;
        this._tryingToReconnect = false;

        try {
            this._hubConnection.stop();
            this.logConnectionStatus('CASLink has disconnected the service connection!  o_O ');
        } finally {
            this.resetConnRetry();
        }
    }

    private resetConnRetry() {
        this.retryDelay = this.initialRetryDelay;
        this.linearRetryCount = 0;
    }

    public startUpdates<T extends keyof typeof SubscriptionIdTypes, R extends (typeof SubscriptionIdTypes[T])>(
        locationId: number,
        subscriptionType: T,
        updateCriteria: R,
        subscriptionState: any
    ): angular.IPromise<void> {

        const defer = this.$q.defer<void>();

        this._connectionPromise.then(() => {
            this._locationHubProxy.invoke('startUpdates', locationId, subscriptionType, updateCriteria, subscriptionState)
                .done(() => {
                    defer.resolve();

                    const subscriptionKey = this.subscriptionKeys.get(subscriptionType);
                    this.logConnectionStatus(`[${subscriptionKey}] CASLink live data stream requested for Location ${locationId}.`);

                    // Record the subscription so that it can be stopped.
                    const subscription = this.subscriptionHandlers[subscriptionKey];

                    if (subscription && subscription.category) {
                        this._currentSubscriptions[subscription.category].push({
                            locationId: locationId,
                            subscriptionState: subscriptionState,
                            subscriptionType: subscriptionType,
                            updateCriteria: updateCriteria,
                        });
                    }
                })
                .fail(() => {
                    const subscriptionKey = this.subscriptionKeys.get(subscriptionType);

                    this.logConnectionStatus(
                        `[${subscriptionKey}] CASLink could not start a live data stream for Location ${locationId}.  :( `
                    );
                    defer.reject();
                });
        });

        return defer.promise;
    }

    public stopUpdates<T extends keyof typeof SubscriptionIdTypes, R extends (typeof SubscriptionIdTypes[T])>(
        locationId: number,
        subscriptionType: T,
        updateCriteria: R
    ): angular.IPromise<void> {

        const defer = this.$q.defer<void>();

        try {
            this._locationHubProxy.invoke('stopUpdates', locationId, subscriptionType, updateCriteria)
                .done(() => {
                    const subscriptionKey = this.subscriptionKeys.get(subscriptionType);
                    const subscription = this.subscriptionHandlers[subscriptionKey];
                    const getters = SubscriptionIdTypeGetters.get(subscriptionType);
                    // Remove this subscription by filtering it out via the appropriate id in the update criteria.
                    if (subscription.category) {
                        this._currentSubscriptions[subscription.category] = this._currentSubscriptions[subscription.category]
                            .filter(s =>
                                s.locationId !== locationId ||
                                s.subscriptionType !== subscriptionType ||
                                getters.some(g => g(s.updateCriteria) !== g(updateCriteria)));
                    }

                    this.logConnectionStatus(`[${subscriptionKey}] CASLink has stopped a live data stream for Location ${locationId}.`);
                    defer.resolve();
                })
                .fail(() => {
                    this.logConnectionStatus(
                        `[${subscriptionType}] CASLink could not stop a live data stream for Location ${locationId}!  o_O`
                    );
                    defer.reject();
                });
        } catch (e) {
            // tslint:disable-next-line:no-console
            console.error(e);
            defer.reject();
        }

        return defer.promise;
    }

    // Dispatches to the handler associated with the received subscription-type.
    private onUpdateSubscription(locationId: number, subscriptionType: string, data: any, sensorInfo: any) {
        this.logSubscriptionResponse(subscriptionType, locationId, data);

        const subscription = this.subscriptionHandlers[subscriptionType];

        if (subscription && subscription.handler) {
            subscription.handler.call(this, locationId, data, sensorInfo);
            if (this.$rootScope && this.$rootScope.$apply) {
                this.$rootScope.$apply();
            }
        }
    }

    private broadcastRtuStatusUpdate(locationId: number, status: string, hardwareInfo: any) {
        let data: CardStatusUpdateInfo = {
            hardwareItemId: hardwareInfo.hardwareItemId,
            locationId: locationId,
            status: status as CardStatus
        };

        this.commonBroadcastService.broadcast(EventTypes.CardStatusUpdate, data);
    }

    private broadcastSensorUpdate(locationId: number, dataPoints: number[][], sensorInfo: any) {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(
                EventTypes.SensorUpdate,
                {
                    dataPoints: dataPoints,
                    locationId: locationId,
                    sensorId: sensorInfo.sensorId
                } as SensorUpdateInfo);
        }
    }

    private broadcastAlertUpdate(locationId: number, dataPoints: number[][], sensorInfo: any) {
        this.commonBroadcastService.broadcast(EventTypes.AlertUpdate, {
            locationId: locationId,
            sensorId: sensorInfo.sensorId
        });
    }

    private broadcastAlertAggregateUpdate(locationId: number, data: any, sensorInfo: any) {
        this.appContextService.updateLocationAlerts(locationId, data);

        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(
                EventTypes.AlertAggregateUpdate,
                {
                    alerts: data,
                    locationId: locationId
                } as AlertAggregateUpdateInfo);
        }
        this.commonBroadcastService.broadcast(EventTypes.AlertAggregateUpdate,
            {
                alerts: data,
                locationId: locationId
            } as AlertAggregateUpdateInfo);
    }

    private broadcastHardwareItemSyncStatusUpdate(locationId: number, data: LoadingStatusUpdateInfo) {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(
                EventTypes.HardwareItemSyncLoadingStatusUpdate,
                data
            );
        }
        this.commonBroadcastService.broadcast(EventTypes.HardwareItemSyncLoadingStatusUpdate, data);
    }

    private broadcastLoadingStatusUpdate(locationId: number, data: any) {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(
                EventTypes.LoadingStatusUpdate,
                data as LoadingStatusUpdateInfo
            );
        }
    }

    private broadcastViewModelUpdate(locationId: number, data: any, sensorInfo: any) {
        let updateInfo = {
            entityId: sensorInfo.identifier.entityId,
            locationId: locationId,
            viewModel: data.result
        } as ViewModelUpdateInfo;

        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(EventTypes.ViewModelUpdate, updateInfo);
        }

        this.commonBroadcastService.broadcast(EventTypes.ViewModelUpdate, updateInfo);
    }

    private broadcastDeviceSyncUpdate(locationId: number, data: any, info: any) {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(
                EventTypes.DeviceSyncUpdate,
                {
                    locationId: locationId,
                    moduleId: data.moduleId
                } as DeviceSyncUpdateInfo
            );
        }
    }

    private broadcastDeviceStatusUpdate(locationId: number, data: any) {
        this.commonBroadcastService.broadcast(EventTypes.DeviceStatusUpdate, data);
    }

    private broadcastDeviceAutoCommission(locationId: number, data: IDeviceAutoCommission) {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(EventTypes.DeviceAutoCommission, data);
        }
        this.commonBroadcastService.broadcast(EventTypes.DeviceAutoCommission, data);
    }

    private broadcastAreaAssignment(locationId: number, data: any) {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(EventTypes.AreaAssignment, data);
        }
    }

    private broadcastModuleRegistrationAlertSubscription(locationId: number, data: any) {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(EventTypes.ModuleRegistrationAlertSubscription, data);
        }
    }

    private jobProfileUpserted(locationId: number, data) {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(EventTypes.JobProfileUpserted, data);
        }
    }

    // ------------------------------------
    // Connection Management
    // ------------------------------------

    // Called when the client detects that the connection to the service
    // has been dropped and is trying to re-establish a connection.
    private reconnecting() {
        this.notifyConnectionStatus('CASLink is attempting to reopen a service connection.');
    }

    // Called if the client was able to re-establish a dropped connection
    // with the server.
    private reconnected() {
        // Resubscribe to all connections on reconnect
        this.resubscribeToAllCategories();

        this.notifyReConnected();
        this.notifyConnectionStatus('CASLink service connection is active.');
    }

    private resubscribeToAllCategories() {
        for (const category in this._currentSubscriptions) {
            if (this._currentSubscriptions.hasOwnProperty(category)) {
                this.restartAllSubscriptions(category);
            }
        }
    }

    // Called when the user logs out of the application or when the
    // connection to the server has been dropped and the client could
    // not re-establish a connection within a specified elapsed time.
    private disconnected() {
        this.notifyDisconnected();
        this.logConnectionStatus('CASLink service connection is offline. Aww-man! :( ');

        // Connection drop detected or reconnect has been
        // aborted.
        if (!this._tryingToReconnect || this._abortRetry) {
            return;
        }

        this.$timeout(() => this.retryConnection(), this.retryDelay);
    }

    private recordConnAttempt() {

        // Check if the process of reconnecting has been
        // aborted (i.e. User logs out);
        if (this._abortRetry) {
            this.resetConnRetry();
            this._abortRetry = false;
            return;
        }

        if (this._tryingToReconnect) {
            this.notifyConnectionStatus('CASLink is attempting to reopen a service connection.');
        } else {
            // No more time remaining.  Publish an event on the bus that
            // the alert connection has been dropped.
            this.resetConnRetry();
            this.notifyConnectionStatus('CASLink service connection is offline.');
        }
    }

    private retryConnection() {
        if (this.linearRetryCount < this.linearRetryLimit) {
            this.linearRetryCount++;
        }
        if (this.linearRetryCount >= this.linearRetryLimit) {
            this.retryDelay *= this.exponentialRetryCoefficient;
        }

        this.connect().then(() => {
            this.resetConnRetry();
            this.notifyReConnected();
        }).catch(() => {
            this.recordConnAttempt();
        });
    }

    private notifyConnectionStatus(message: string) {
        this.logConnectionStatus(message);
        if (this.showToasts) {
            this.toastService.create({ message: message }, false, this.toastDuration);
        }
    }

    private notifyConnected() {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(EventTypes.ConnectedToServer);
        }
        this.commonBroadcastService.broadcast(EventTypes.ConnectedToServer);
    }

    private notifyDisconnected() {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(EventTypes.DisconnectedFromServer);
        }
        this.commonBroadcastService.broadcast(EventTypes.DisconnectedFromServer);
    }

    private notifyReConnected() {
        if (this.$rootScope && this.$rootScope.publish) {
            this.$rootScope.publish(EventTypes.ReconnectedToServer);
        }
        this.commonBroadcastService.broadcast(EventTypes.ReconnectedToServer);
    }

    // ------------------------------------
    // Logging
    // ------------------------------------
    private logConnectionStatus(statusMsg: string) {
        this.$log.info({
            message: 'CASLink Service Connection Status',
            status: statusMsg
        });
    }

    private logSubscriptionResponse(subscriptionType: string, locationId: number, data: any) {
        this.$log.info({
            data: data,
            message: `[${subscriptionType}] subscription update received for Location: ${locationId}.`
        });
    }
}
